/* eslint-disable react/jsx-props-no-spreading */
/**
 * @description Renders the personnel form and list of the assign contract page
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import times from 'lodash/times';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';

import useClientSidePagination from '@seccon/shared/ui/hooks/useClientSidePagination';
import withErrorBoundary from '@seccon/shared/ui/hocs/with-error-boundary';
import NoResults from '@seccon/shared/ui/components/NoResults';

import SelectAllBox from './SelectAllBox';
import FormFooter from './FormFooter';
import useUnassignedPersonnel from '../hooks/useUnassignedPersonnel';
import useInitializePersonnelForm from '../hooks/useInitializePersonnelForm';
import useAssignPersonnel from '../hooks/useAssignPersonnel';
import PersonnelListItem, { PersonnelListItemSkeleton } from './PersonnelListItem';

const styles = {
  personnelContainer: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: 2,
    width: '100%',

    '& > .personnel-settings': {
      flex: 1
    }
  },
  pagination: {
    m: 0,
    overflow: 'visible'
  },
  paginationContainer: {
    backgroundColor: 'grey.100',
    padding: 1
  }
};

/**
 * Validates the personnel selections by making sure at least one personnel (hidden or not) is selected
 * @param {object} selectedPersonnel - a map of selected personnel, keyed by their personnel id
 * @param {object} hiddenSelectedPersonnel - a map of selected personnel currently hidden from the form, keyed by their personnel id
 * @returns {string} - error message
 */
function validateSelection (selectedPersonnel, hiddenSelectedPersonnel) {
  if (Object.keys(selectedPersonnel ?? {}).length === 0 && Object.keys(hiddenSelectedPersonnel ?? {}).length === 0) {
    return 'One value must be selected'
  }

  return null;
}

const PersonnelList = () => {
  const { contractId } = useParams();
  const unassignedPersonnel = useUnassignedPersonnel();

  const form = useForm({
    mode: 'onChange'
  });

  // Register a field array named personnel, which will contain a list of all the personnel setting fields
  const { fields: personnel } = useFieldArray({ control: form.control, name: 'personnel' })

  // Initialize the form fields based changes to the contract personnel state and the access required options
  useInitializePersonnelForm(form, unassignedPersonnel.data);

  // Paginate the personnel list field array, so we don't display all the personnel all at once
  const {
    handleChangeRowsPerPage,
    handlePageChange,
    page,
    pageData,
    rowsPerPage,
    total
  } = useClientSidePagination({
    data: personnel
  });

  const handleSubmit = useAssignPersonnel();

  // Disable the submit button if the form is submitting, pristine, or not valid
  const { isDirty, isValid, isSubmitting } = form.formState;
  const disableSubmit = isSubmitting || !isDirty || !isValid;

  /**
   * Handles cancel button clicks by sending the user back to the original page
   */
  function handleCancel () {
    window.location = `/contract/show/${ contractId }`;
  }

  function renderContent () {
    // If the personnel list is loading, display loading skeletons
    if (unassignedPersonnel.isLoading) {
      return (
        <List>
          {times(3, Boolean).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <PersonnelListItemSkeleton key={index} />
          ))}
        </List>
      );
    }

    // If an error occured loading the personnel list, display a warning overlay
    if (unassignedPersonnel.isError) {
      return (
        <NoResults Icon={WarningAmberIcon} sx={{ fontSize: '1.5rem' }} variant='overlay'>Unable to load personnel list</NoResults>
      );
    }

    // If the personnel list is empty, display an empty overlay
    if (unassignedPersonnel.data.length === 0) {
      return (
        <NoResults sx={{ fontSize: '1.5rem' }} variant='overlay'>No personnel could be found</NoResults>
      );
    }

    return (
      <>
        <List>
          <ListItem sx={{ ...styles.personnelContainer, alignItems: 'center' }}>
            <SelectAllBox />
            <Typography className='personnel-settings' variant='h6' component='p'>Assign All</Typography>
          </ListItem>
          {pageData.map((person, dataIndex) => {
            // Determine the index of the personnel in the field array based on the data index, current page, and current page size
            const index = (page * rowsPerPage) + dataIndex;
            return <PersonnelListItem key={person.id} person={person} index={index} />;
          })}
        </List>
        <Paper elevation={2} sx={styles.paginationContainer}>
          <TablePagination
            component='div'
            count={total}
            page={page}
            onPageChange={(_, value) => handlePageChange(value)}
            onRowsPerPageChange={(evt) => handleChangeRowsPerPage(parseInt(evt.target.value, 10))}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            sx={styles.pagination}
          />
        </Paper>
      </>
    );
  }

  return (
    <FormProvider {...form}>
      <input
        type='hidden'
        {...form.register('hiddenSelectedPersonnel', {
          value: {},
          validate: (value, values) => validateSelection(values.selectedPersonnel, value)
        })}
      />
      <input
        type='hidden'
        {...form.register('selectedPersonnel', {
          value: {},
          validate: (value, values) => validateSelection(value, values.hiddenSelectedPersonnel)
        })}
      />
      {renderContent()}
      <FormFooter
        disabled={disableSubmit}
        isSubmitting={isSubmitting}
        onSubmit={form.handleSubmit(handleSubmit)}
        onCancel={() => handleCancel()}
      />
    </FormProvider>
  );
};

export default withErrorBoundary(PersonnelList);
